import { createSlice } from "@reduxjs/toolkit";
import {
  clearAllPortalSessions,
  removePortalSession,
  upsertPortalSession,
} from "./portalAuthUtils";

const initialState = {
  activeSubdomain: null,
  sessions: {},
  hydrated: false,
};

const portalAuthSlice = createSlice({
  name: "portalAuth",
  initialState,
  reducers: {
    hydratePortalSessions: (state, action) => {
      state.sessions = action.payload || {};
      state.hydrated = true;
    },
    setActivePortalSubdomain: (state, action) => {
      state.activeSubdomain = action.payload;
    },
    portalLoginSuccess: (state, action) => {
      const {
        subdomain,
        accessToken,
        user,
        customerContact,
        salesRep,
        portalId,
        currency,
        paymentTerm,
        decimalPrecision,
        portalConfig,
      } = action.payload;

      // portalLoginSuccess — omit undefined keys
      const sessionData = {
        isAuthenticated: true,
        accessToken,
        portalSlug: subdomain,
      };
      if (user !== undefined) sessionData.user = user;
      if (customerContact !== undefined) sessionData.customerContact = customerContact;
      if (salesRep !== undefined) sessionData.salesRep = salesRep;
      if (portalId !== undefined) sessionData.portalId = portalId;
      if (currency !== undefined) sessionData.currency = currency;
      if (paymentTerm !== undefined) sessionData.paymentTerm = paymentTerm;
      if (decimalPrecision !== undefined) sessionData.decimalPrecision = decimalPrecision;
      if (portalConfig !== undefined) sessionData.portalConfig = portalConfig;

      state.sessions[subdomain] = { ...state.sessions[subdomain], ...sessionData };
      upsertPortalSession(subdomain, sessionData);
    },
    portalLogout: (state, action) => {
      const { subdomain, isCrossTab } = action.payload;
      if (subdomain) {
        delete state.sessions[subdomain];
        removePortalSession(subdomain);
        
        // Prevent infinite ping-pong loop between tabs
        if (!isCrossTab) {
          try {
            localStorage.setItem(`portalAuthOut:${subdomain}`, Date.now().toString());
          } catch (e) {
            console.error("Failed to set localStorage portalAuthOut", e);
          }
        }
      }
    },
    portalLogoutAll: state => {
      state.sessions = {};
      clearAllPortalSessions();
    },
  },
});

export const {
  hydratePortalSessions,
  setActivePortalSubdomain,
  portalLoginSuccess,
  portalLogout,
  portalLogoutAll,
} = portalAuthSlice.actions;

export const selectPortalSession = (state, subdomain) => state.portalAuth.sessions[subdomain];
export const selectIsPortalAuthenticated = (state, subdomain) =>
  !!state.portalAuth.sessions[subdomain]?.isAuthenticated;
export const selectPortalAccessToken = (state, subdomain) =>
  state.portalAuth.sessions[subdomain]?.accessToken;
export const selectPortalCustomerContact = (state, subdomain) =>
  state.portalAuth.sessions[subdomain]?.customerContact;
export const selectPortalSalesRep = (state, subdomain) =>
  state.portalAuth.sessions[subdomain]?.salesRep;
export const selectActivePortalSubdomain = state => state.portalAuth.activeSubdomain;

export default portalAuthSlice.reducer;
